import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import firebaseConfig from '../../config';

const { databaseId } = firebaseConfig;

function QuestionsFactory(firebase) {
  const addComment = async ({
    sessionId,
    questionId,
    user,
    html,
    parentCommentId,
    language
  }) => {
    const ref = collection(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments'
    );
    return addDoc(ref, {
      uid: user.uid,
      name: user.name,
      avatarUrl: user.avatarUrl,
      html,
      sessionId,
      questionId,
      timestamp: serverTimestamp(),
      parentCommentId,
      // The author's active UI language — enables the phase-2 on-demand
      // comment translation (spec §6.3). Null for legacy/unknown.
      language: language ?? null
    });
  };

  const editComment = async ({ sessionId, questionId, commentId, html }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments',
      commentId
    );
    return updateDoc(ref, {
      html,
      // Cached translations of the previous wording are stale the moment an
      // edit lands (§6.3) — readers re-translate on demand.
      translationCache: deleteField()
    });
  };

  // On-demand comment translation (§6.3) — returns { html, cached }; the
  // Comment component checks the doc's translationCache first, so this is
  // only reached on a cache miss.
  const translateComment = async ({
    sessionId,
    questionId,
    commentId,
    targetLanguage
  }) => {
    const translateCommentCallable = httpsCallable(
      firebase.functions,
      'comments-translate'
    );
    return translateCommentCallable({
      sessionId,
      questionId,
      commentId,
      targetLanguage,
      databaseId
    });
  };

  /**
   * Translates every comment on a question that is missing the target
   * language, in one request per chunk server-side rather than one per
   * comment. Results land in the same `translationCache` the per-comment
   * path uses, and the live subscription streams them back — so nothing is
   * read from the return value except counts.
   */
  const translateCommentsBatch = async ({
    sessionId,
    questionId,
    targetLanguage
  }) => {
    const translateCommentsBatchCallable = httpsCallable(
      firebase.functions,
      'comments-translateBatch'
    );
    return translateCommentsBatchCallable({
      sessionId,
      questionId,
      targetLanguage,
      databaseId
    });
  };

  const deleteComment = async ({ sessionId, questionId, commentId }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments',
      commentId
    );
    return deleteDoc(ref);
  };

  const addQuestion = async ({
    sessionId,
    questionId,
    html,
    order,
    isVisible,
    language
  }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );

    const questionData = {
      html,
      isVisible,
      order,
      questionId,
      sessionId,
      // The author's active UI language, mirroring comments (§6.3).
      //
      // INFORMATIONAL ONLY — nothing may branch on this. It is a guess: the
      // switcher's position is merely correlated with the language someone
      // types in, and an author is free to write in any language. It was
      // briefly load-bearing (2026-08-17: questions-translate skipped it as
      // the source) and that was reverted, because it hung a permanent,
      // silent, per-language failure off a guess. questions-translate now
      // translates into every enabled language regardless, which lands
      // correct whatever this field happens to say.
      language: language ?? null,
      timestamp: serverTimestamp()
    };

    // If creating as visible, set questionBecameVisibleAt
    if (isVisible) {
      questionData.questionBecameVisibleAt = serverTimestamp();
    }

    return setDoc(ref, questionData);
  };

  const editQuestion = async ({ sessionId, questionId, html, language }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );
    return updateDoc(ref, {
      html,
      // Re-stamped so the record tracks the current text rather than whoever
      // first wrote it. Informational only — see addQuestion.
      language: language ?? null,
      // Atomic invalidation: a stale translation of the previous wording is
      // worse than a brief fallback to the source language (§6.2). The
      // translateQuestion callable repopulates the map.
      htmlTranslations: deleteField()
    });
  };

  // Fire-and-forget from call sites — fills htmlTranslations server-side for
  // every language in config/tenant.enabledLanguages (no-op on
  // single-language instances).
  const translateQuestion = async ({ sessionId, questionId }) => {
    const translateQuestionCallable = httpsCallable(
      firebase.functions,
      'questions-translate'
    );
    return translateQuestionCallable({ sessionId, questionId, databaseId });
  };

  const updateQuestionVisibility = async ({
    sessionId,
    questionId,
    isVisible
  }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );

    const updateData = { isVisible };

    if (isVisible === true) {
      // Setting to visible: Always set questionBecameVisibleAt to now
      updateData.questionBecameVisibleAt = serverTimestamp();
      // A manual reveal supersedes any pending schedule — clearing it here
      // keeps the scheduler from re-processing (and re-stamping) the question.
      updateData.scheduledVisibleAt = deleteField();
      updateData.scheduledVisibleTimezone = deleteField();
      updateData.notifyOnReveal = deleteField();
    } else {
      // Setting to invisible: Delete questionBecameVisibleAt
      updateData.questionBecameVisibleAt = deleteField();
    }

    return updateDoc(ref, updateData);
  };

  /**
   * Schedules a hidden question's reveal. `scheduledVisibleAt` is a JS Date
   * holding the UTC instant (the caller has already resolved the moderator's
   * wall-clock choice through the chosen zone); `timezone` is kept only so
   * the UI can show the schedule back in the zone it was entered in. The
   * flip itself is performed server-side by questions-revealScheduled, which
   * also emails the session's participants when `notifyOnReveal` is set.
   */
  const scheduleQuestionVisibility = async ({
    sessionId,
    questionId,
    scheduledVisibleAt,
    timezone,
    notifyOnReveal = false
  }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );
    return updateDoc(ref, {
      isVisible: false,
      questionBecameVisibleAt: deleteField(),
      scheduledVisibleAt: Timestamp.fromDate(scheduledVisibleAt),
      scheduledVisibleTimezone: timezone,
      notifyOnReveal: Boolean(notifyOnReveal)
    });
  };

  const clearScheduledVisibility = async ({ sessionId, questionId }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );
    return updateDoc(ref, {
      scheduledVisibleAt: deleteField(),
      scheduledVisibleTimezone: deleteField(),
      notifyOnReveal: deleteField()
    });
  };

  const subscribeToAllSessionQuestions = ({ sessionId, snapshot }) => {
    const ref = collection(firebase.fsdb, 'sessions', sessionId, 'questions');
    return onSnapshot(query(ref, orderBy('order', 'asc')), snapshot);
  };

  const subscribeToQuestion = ({ sessionId, snapshot, questionId }) => {
    const ref = collection(firebase.fsdb, 'sessions', sessionId, 'questions');
    return onSnapshot(
      query(
        ref,
        where('questionId', '==', questionId),
        where('isVisible', '==', true),
        orderBy('order', 'asc')
      ),
      snapshot
    );
  };

  const subscribeToComments = ({ sessionId, questionId, snapshot }) => {
    const ref = collection(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments'
    );
    return onSnapshot(
      query(
        ref,
        where('questionId', '==', questionId),
        orderBy('timestamp', 'asc')
      ),
      snapshot
    );
  };

  const subscribeToAllSessionComments = ({ sessionId, snapshot }) => {
    const ref = collectionGroup(firebase.fsdb, 'comments');
    return onSnapshot(
      query(
        ref,
        where('sessionId', '==', sessionId),
        orderBy('timestamp', 'asc')
      ),
      snapshot
    );
  };

  return {
    addComment,
    addQuestion,
    clearScheduledVisibility,
    deleteComment,
    editComment,
    editQuestion,
    scheduleQuestionVisibility,
    subscribeToAllSessionComments,
    subscribeToAllSessionQuestions,
    subscribeToComments,
    subscribeToQuestion,
    translateComment,
    translateCommentsBatch,
    translateQuestion,
    updateQuestionVisibility
  };
}

export default QuestionsFactory;
